import { FormEvent, Fragment, FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AddressField,
    ChatIcon,
    FieldEventDataProps,
    GiftCard,
    isBornholm,
    OverlayDialog,
    RenderHtml
} from '@flos/react-components';
import { Button, CircularProgress, Grid, Typography } from '@material-ui/core';
import { errorTypes, track } from 'c9-tracking';

import { ROUTE_NAMES } from '../../router/RouterApp';
import { useGlobalDispatch, useGlobalState } from '../../common/context/AppContext';
import { IAppProps, SCREEN_NAME } from '../../common/interface/IAppProvider';
import {
    CustomerContentStatusMessage,
    EMAIL_EVENT,
    IGetCustomerContentStatus,
    MAX_AGE,
    MIN_AGE
} from '../../common/interface/IPayload';
import getHippoText from '../../common/services/HippoTextService';
import { getContactUsURL, getCurrentStep, getStepCount } from '../../common/utils/helpers/GenericHelpers';
import { fetchGiftCardDetails, getCustomerContentStatus, getCustomerMarking } from '../../common/async/AsyncCalls';
import { useStakeholderInfo } from '../../common/customHooks/useStakeHolderInfo';

import { GoToMitModal } from '../../components/modal/GoToMitModal';

import { baseStyles } from '../../theme/base.styles';

interface IModalState {
    modalStatus: boolean;
    modalContent: JSX.Element;
}

const AddressScreenView: FunctionComponent<IAppProps> = (props): JSX.Element => {
    const baseClasses = baseStyles();
    const globalState = useGlobalState();
    const appDispatch = useGlobalDispatch();
    const [error, setError] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isWaitingCM, setIsWaitingCM] = useState(false);
    const [isWaiting, setIsWaiting] = useState(false);
    const [address, setAddress] = useState<FieldEventDataProps>(globalState.address);
    const [showMitModal, setShowMitModal] = useState<boolean>(false);
    const navigate = useNavigate();
    const hippoTexts = getHippoText(globalState.section, 'onsa.intro');
    const ageInfo = useStakeholderInfo(props);

    const fetchGiftCard = useCallback(async () => {
        if (globalState.giftCard === null && !isWaiting) {
            try {
                setIsWaiting(true);
                const response = await fetchGiftCardDetails(props, appDispatch);
                track.info([{ key: 'gift card status for content: ', value: response?.status }]);

                appDispatch({
                    type: SCREEN_NAME.SET_GIFT_CARD,
                    payload: { ...response, amount: new Intl.NumberFormat('da-DK').format(response.amount) }
                });
                setIsWaiting(false);
            } catch (e) {
                track.fault('get gift card status for content failed', errorTypes.tech, 'giftCard');
                appDispatch({
                    type: SCREEN_NAME.SET_GIFT_CARD,
                    payload: { status: 'inactive', key: 'content', amount: '0', campaign: 'default' }
                });
            }
        }
    }, [globalState.giftCard, props, appDispatch, isWaiting]);

    const fetchCustomerMarking = useCallback(async () => {
        if (!isWaitingCM && globalState.customerHasMarking === null && window.topContext.user.isLoggedOn) {
            try {
                setIsWaitingCM(true);
                const response = await getCustomerMarking(props, appDispatch);
                appDispatch({ type: SCREEN_NAME.SET_CUSTOMER_HAS_MARKING, payload: !response.canProceed });
                track.info([{ key: 'Customer marking: ', value: !response.canProceed }]);
                setIsWaitingCM(false);
                setIsOpen(true);
            } catch (e) {
                setIsWaitingCM(false);
                appDispatch({ type: SCREEN_NAME.SET_CUSTOMER_HAS_MARKING, payload: false });
                track.fault('get customer marking status failed', errorTypes.tech, 'Customer Marking');
            }
        }
    }, [isWaitingCM, globalState.customerHasMarking, props, appDispatch]);

    const closeOverlay = () => {
        setIsOpen(false);
        window.location.assign(window.location.origin + '/forsikringer/indboforsikring/');
    };

    const overlayBBRSubmitEventHandler = (e: FormEvent) => {
        e.preventDefault();
        track.step(getCurrentStep(ROUTE_NAMES.INTRO), getStepCount());
        window.location.assign(getContactUsURL());
    };

    const [modalBBR, setModalBBR] = useState<IModalState>({
        modalStatus: false,
        modalContent: (
            <Fragment>
                <RenderHtml html={globalState.contentBlocks.addresspage_not_in_bbr} />
                <form
                    onSubmit={overlayBBRSubmitEventHandler}
                    name={`onsa-content-form-addr-bbr`}
                    id={`onsa-content-form-addr-bbr`}
                >
                    <Button type={'submit'} fullWidth variant="contained">
                        {hippoTexts['contactus']}
                    </Button>
                </form>
            </Fragment>
        )
    });

    const toggleModalBBR = (show: boolean) => {
        setModalBBR((prev) => {
            return { ...prev, modalStatus: show };
        });
    };

    useEffect(() => {
        if (globalState.initialState) {
            // reset state
            appDispatch({ type: SCREEN_NAME.SET_INITIAL_STATE, payload: false });
        }

        if (!globalState.initialState && !globalState.giftCard && globalState.customerHasMarking !== true) {
            fetchGiftCard();
        }

        fetchCustomerMarking();
    }, [
        appDispatch,
        globalState.initialState,
        globalState.giftCard,
        globalState.customerHasMarking,
        fetchGiftCard,
        fetchCustomerMarking
    ]);

    // On getStakeHolderInfo success, check age
    useEffect(() => {
        if (ageInfo.progress === 'logged-on-success') {
            if (ageInfo.value < MIN_AGE || ageInfo.value > MAX_AGE) {
                track.fault(`age validation failed, ${ageInfo.value}`, errorTypes.rule, 'User-data age');
                navigate(ROUTE_NAMES.AGE_ERROR);
            }
        }
    }, [ageInfo, navigate, appDispatch]);

    const fieldEventHandler = (val: FieldEventDataProps | string) => {
        if (typeof val === 'string') {
            appDispatch({ type: SCREEN_NAME.SET_EMAIL_EVENT, payload: EMAIL_EVENT.GENERAL_ERROR });
            appDispatch({ type: SCREEN_NAME.SET_ERROR, payload: true });
        } else if (val.status === 'valid') {
            setError(false);
            setAddress(val);
        } else if (val?.status === 'empty') {
            setError(false);
        } else {
            setError(true);
        }
    };

    function submitHandler(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (
            address.status !== 'valid' ||
            address.address === '' ||
            address.kvhx === undefined ||
            address.inBBR === false
        ) {
            track.fault('address validation failed', errorTypes.tech, 'User-data Address');
            if (address.inBBR === false) {
                setModalBBR((prev) => {
                    return { ...prev, modalStatus: true };
                });
            }
            setError(true);
        } else {
            appDispatch({ type: SCREEN_NAME.ADDRESS, payload: { address: address } });
            track.step(getCurrentStep(ROUTE_NAMES.INTRO), getStepCount());
            if (isBornholm(address.addressInfo.zipCode)) {
                navigate(ROUTE_NAMES.BORNHOLM);
            } else {
                if (!window.topContext.user.isLoggedOn) {
                    props.c9log.info(`Address selected is: ${JSON.stringify(address)}`, 'AddressScreen');
                    appDispatch({ type: SCREEN_NAME.SET_STATE_CHANGE, payload: true });
                    navigate(ROUTE_NAMES.ALREADY_CUSTOMER);
                } else {
                    setIsWaiting(true);
                    getCustomerContentStatus(props, appDispatch, address.kvhx)
                        .then((x: IGetCustomerContentStatus) => {
                            appDispatch({ type: SCREEN_NAME.CUSTOMER_CONTENT_STATUS, payload: x });
                            if (x.canProceed) {
                                //handle moving scenario
                                if (x.movingScenario) {
                                    track.info([
                                        {
                                            key: 'moving-scenario-started',
                                            value: 'Logged in customer proceeded flow in moving scenario'
                                        }
                                    ]);
                                    appDispatch({
                                        type: SCREEN_NAME.SET_EMAIL_EVENT,
                                        payload: EMAIL_EVENT.EXISTING_CONTENT_DIFF_ADDRESS
                                    });
                                    navigate(ROUTE_NAMES.HOUSEHOLD);
                                }
                                if (!x.movingScenario) {
                                    navigate(ROUTE_NAMES.ALREADY_CUSTOMER);
                                }
                                appDispatch({ type: SCREEN_NAME.SET_STATE_CHANGE, payload: true });
                            } else {
                                //tries to calculate on already insured address
                                if (
                                    x.hasInsurance === 'content' &&
                                    x.message ===
                                        CustomerContentStatusMessage.TRIES_TO_CALCULATE_ON_ADDRESS_WITH_CONTENT_INSURANCE
                                ) {
                                    //show modal with link to mit topdanmark
                                    setShowMitModal(true);
                                    track.fault(
                                        `address check failed, current address same as selected`,
                                        errorTypes.rule,
                                        'User-data address'
                                    );
                                } else {
                                    getCustomerMarking(props, appDispatch).then((response) => {
                                        if (!response.canProceed) {
                                            navigate(ROUTE_NAMES.CALL_ME);
                                            track.fault(
                                                `Customer can't proceed due to marking`,
                                                errorTypes.rule,
                                                'Customer marking'
                                            );
                                        }
                                    });
                                    navigate(ROUTE_NAMES.CALL_ME);
                                }
                            }
                        })
                        .finally(() => {
                            setIsWaiting(false);
                        });
                }
            }
        }
    }

    if (
        ageInfo.progress !== 'none' &&
        ageInfo.progress !== 'in-progress' &&
        globalState.giftCard &&
        globalState.customerHasMarking !== true
    ) {
        return (
            <Grid container spacing={1} className={baseClasses.main}>
                <GoToMitModal show={showMitModal} modalStateHandler={setShowMitModal} />
                <OverlayDialog
                    open={modalBBR.modalStatus}
                    onClose={toggleModalBBR.bind(null, false)}
                    transitionDuration={0}
                >
                    {modalBBR.modalContent}
                </OverlayDialog>
                <Grid item xs={12} md={12}>
                    <Typography variant="h1" color="textPrimary" className={baseClasses.h1}>
                        {hippoTexts['intro.heading']}
                    </Typography>
                    <ChatIcon />
                    <Typography variant="subtitle1" color="textPrimary" paragraph className={baseClasses.spacingMedium}>
                        {hippoTexts['message0']}
                    </Typography>
                    <Typography variant="subtitle1" color="textPrimary" paragraph className={baseClasses.spacingMedium}>
                        {hippoTexts['message1']}
                    </Typography>

                    {globalState.giftCard.status === 'active' && (
                        <GiftCard
                            productType="CONTENT"
                            amount={globalState.giftCard.amount}
                            campaign={globalState.giftCard.campaign}
                        />
                    )}

                    <Typography variant="subtitle1" color="textPrimary" paragraph className={baseClasses.spacingNormal}>
                        {hippoTexts['message2']}
                    </Typography>

                    <form name={`onsa-content-form-address`} id={`onsa-content-form-address`} onSubmit={submitHandler}>
                        <AddressField
                            defaultValue={address.address}
                            error={error}
                            label={'Adresse'}
                            id={'tdk-address-autocomplete'}
                            errorText={'Ugyldig adresse'}
                            fieldEvent={fieldEventHandler}
                        />
                        <Button type="submit" fullWidth variant="contained">
                            {isWaiting ? (
                                <CircularProgress role="spinner" size={26} className={baseClasses.buttonProgress} />
                            ) : (
                                hippoTexts.button
                            )}
                        </Button>
                    </form>
                </Grid>
            </Grid>
        );
    } else if (globalState.customerHasMarking) {
        return (
            <OverlayDialog open={isOpen} onClose={closeOverlay} transitionDuration={0}>
                <RenderHtml html={globalState.contentBlocks['customer-rejection']} />
            </OverlayDialog>
        );
    } else {
        return <Fragment />;
    }
};

export default AddressScreenView;
